/* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-call */
import type {
  AddToCalendarDelegate,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  PlatformDelegate,
} from '@/features/shell/platform/types'
import { DefaultPlatformDelegate } from '@/features/shell/platform/DefaultPlatformDelegate'
import { observable, runInAction } from 'mobx'

/**
 * Platform delegate that allows interfacing with native hosts.
 * Falls back to the default delegate for non-registered handlers.
 */
export class NativePlatformDelegate implements PlatformDelegate {
  static readonly instance: PlatformDelegate = new NativePlatformDelegate()

  @observable
  private accessor _externalUrlDelegate: ExternalUrlDelegate | null = null

  @observable
  private accessor _documentDownloadDelegate: DocumentDownloadDelegate | null =
    null

  @observable
  private accessor _addToCalendarDelegate: AddToCalendarDelegate | null = null

  getExternalUrlDelegate() {
    return (
      this._externalUrlDelegate ??
      DefaultPlatformDelegate.instance.getExternalUrlDelegate()
    )
  }

  getDocumentDownloadDelegate() {
    return (
      this._documentDownloadDelegate ??
      DefaultPlatformDelegate.instance.getDocumentDownloadDelegate()
    )
  }

  getAddToCalendarDelegate() {
    return (
      this._addToCalendarDelegate ??
      DefaultPlatformDelegate.instance.getAddToCalendarDelegate()
    )
  }

  constructor() {
    this.getExternalUrlDelegate = this.getExternalUrlDelegate.bind(this)
    this.getDocumentDownloadDelegate =
      this.getDocumentDownloadDelegate.bind(this)
    this.getAddToCalendarDelegate = this.getAddToCalendarDelegate.bind(this)
    // Set up `sendNativeMessage` listeners for registering custom delegates.
    window.addEventListener('message', this._onMessage.bind(this))
    // Emit an event notifying the host that they may now register delegates.
    sendNativeMessage({ type: 'tf-ready' })
  }

  /**
   * Handle messages from the window for registering custom delegates.
   *
   * @param evt
   * @private
   */
  private _onMessage(evt: MessageEvent) {
    try {
      let data: any = evt.data
      if (typeof evt.data === 'string') {
        data = JSON.parse(evt.data)
      }

      if (!('type' in data)) {
        return
      }

      runInAction(() => {
        switch (data.type) {
          case 'register-external-url-delegate': {
            this._externalUrlDelegate = new NativeExternalUrlDelegate()
            break
          }
          case 'register-document-download-delegate': {
            this._documentDownloadDelegate =
              new NativeDocumentDownloadDelegate()
            break
          }
          case 'register-add-to-calendar-delegate': {
            this._addToCalendarDelegate = new NativeAddToCalendarDelegate()
            break
          }
        }
      })
    } catch (err: unknown) {
      console.error('Error handling window message event', evt.data, err)
    }
  }

  /**
   * Checks whether the native platform delegate is supported.
   */
  static get isSupported() {
    return isEmbeddedInNative()
  }
}

class NativeExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(): ExternalUrlOpenHandle {
    return {
      open(url: string) {
        sendNativeMessage({ type: 'open-external-url', url })
      },
      abort() {
        // no-op
      },
    }
  }
}

class NativeDocumentDownloadDelegate implements DocumentDownloadDelegate {
  requiresFormSubmission = false

  download(url: string): void {
    sendNativeMessage({ type: 'download-document', url })
  }
}

class NativeAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    const reader = new FileReader()
    reader.onloadend = () =>
      sendNativeMessage({
        type: 'add-to-calendar',
        icsDataUrl: reader.result,
      })

    reader.readAsDataURL(icsFile)
  }
}

/**
 * Checks whether we are embedded in a native app.
 */
function isEmbeddedInNative() {
  return Boolean(
    // @ts-ignore
    window.ReactNativeWebView || window.flutter_inappwebview || window.webkit,
  )
}

/**
 * Sends a message from a web view environment to its native container app
 * (React Native, Flutter, Capacitor, etc.) via the appropriate bridge.
 *
 * @param {object} data The message payload to send. Will be stringified.
 */
function sendNativeMessage(data: any) {
  const message = JSON.stringify(data)

  // React Native (iOS and Android)
  // @ts-ignore
  if (window.ReactNativeWebView) {
    // @ts-ignore
    window.ReactNativeWebView.postMessage(message)
    return
  }

  // Flutter InAppWebView (Android) - uses a slightly different API style
  // @ts-ignore
  if (window.flutter_inappwebview) {
    // Flutter usually expects specific handler names, adjust as needed in your RN code
    // @ts-ignore
    window.flutter_inappwebview.callHandler('taxfyleMessageHandler', message)
    return
  }

  // iOS WKWebView (Fallback method for pure native iOS)
  if (
    // @ts-ignore
    window.webkit?.messageHandlers?.nativeApp
  ) {
    // iOS WKWebView bridge typically expects an object, not a stringified JSON string
    // @ts-ignore
    window.webkit.messageHandlers.nativeApp.postMessage(data)
    return
  }

  // Standard Browser Window (Fallback for web-to-web communication)
  // If running in a browser without a native bridge, use standard window.postMessage
  // This assumes a parent window is listening.
  // We can just pass the raw `data` along without serializing it.
  window.postMessage(data, '*')
}
